import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import FilterButton from '../../_common/FilterButton/FilterButton';
import classes from './refills.module.scss';
import InputField from '../../_common/Form/TextFields/InputField';
import Select from '../../_common/Form/Selects/Select/Select';
import { RangeDatePicker } from '../../_common/Form/DatePickers/RangeDatePicker/RangeDatePicker';
import { specOptions } from '../../../utils/data/data';
import accountHelpers from '../../../utils/accountHelpers';
import { useQuery } from '../../../utils/hooks/useQuery';
import utils from '../../../utils/utils';
import { changeMenuPath } from '../../../store/actions/menu';
import { getRefillsRequest } from '../../../store/actions/refills';
import { getHealthRequest } from '../../../store/actions/healths';
import { getSelectRepListRequest } from '../../../store/actions/users';
import RefilesTable from './RefillsTable/RefilesTable';
import Loader from '../../_common/Loader/Loader';

const typeData = [
  {
    title: 'Ok to Refill',
    key: 'status',
    value: 'Refill',
  },
  {
    title: 'Refill Request',
    key: 'status',
    value: 'Renew',
  },
];

const header = ['Process On', 'Physician', 'Patient', 'Medication', 'RX Number', 'Last Faxed', 'Fax'];

const thisMonthStartDay = moment().startOf('month').format('YYYY-MM-DD');
const thisMonthEndDate = moment().format('YYYY-MM-DD');

const healthPlanLimit = 20;

const collectRequestData = (requestData) => {
  const data = utils.deleteEmptyKeys(requestData);

  if (data.start) {
    if (!data.end) {
      data.end = moment(data.start).add(1, 'day').format('YYYY-MM-DD');
    } else {
      data.end = moment(data.end).format('YYYY-MM-DD');
    }
  }

  return data;
};

let timeout;

function Refills() {
  const dispatch = useDispatch();
  const { refillsList, refillTotalPage, requestStatus } = useSelector((store) => store.refills);
  const { selectRepList, pageCount, currentPage } = useSelector((store) => store.users);
  const { selectHealthList, healthTotalPage, healthCurrentPage } = useSelector((store) => store.healths);

  const isAdmin = accountHelpers.isInRole('Admin');

  const { query, setQuery, queryToStringify } = useQuery();
  const { pathname } = useLocation();

  const [requestData, setRequestData] = useState(
    {
      status: query.status || 'Refill',
      textSearch: query.textSearch || '',
      rep: query.rep || '',
      spec: query.spec || '',
      start: query.start || '',
      end: !query.start && query.end ? '' : (query.end || ''),
      hub: query.hub || '',
      page: query.page || 1,
      limit: query.limit || 15,
      userId: accountHelpers.getUser().id,
    },
  );

  const {
    page, limit, status, textSearch, spec, hub,
  } = requestData;

  useEffect(() => {
    const newQuery = utils.deleteEmptyKeys({ ...requestData });
    delete newQuery.userId;

    dispatch(changeMenuPath('Refills', `${pathname}${queryToStringify(newQuery)}`));
    setQuery(newQuery);
  }, [requestData]);

  useEffect(() => {
    dispatch(getSelectRepListRequest({ limit: 20, role: 'Rep' }));

    if (isAdmin) dispatch(getHealthRequest({ limit: healthPlanLimit }));

    dispatch(getRefillsRequest(collectRequestData(requestData)));
  }, []);

  const onTypeClick = (key, value) => {
    setRequestData((prev) => ({ ...prev, [key]: value, page: 1 }));

    const newQuery = collectRequestData({ ...requestData, [key]: value, page: 1 });

    dispatch(getRefillsRequest(newQuery));
  };

  const onSearchChange = (value) => {
    setRequestData((prev) => ({ ...prev, textSearch: value, page: 1 }));

    const newQuery = collectRequestData({ ...requestData, textSearch: value, page: 1 });

    clearTimeout(timeout);

    timeout = setTimeout(() => {
      dispatch(getRefillsRequest(newQuery));
    }, 500);
  };

  const nextOptionListRequest = async (pageRep) => {
    await dispatch(getSelectRepListRequest({ page: pageRep, limit: 20, role: 'Rep' }));
  };

  const nextHealthPlanOptionListRequest = async (pageHealth) => {
    await dispatch(getHealthRequest({ page: pageHealth, limit: healthPlanLimit }));
  };

  const onChangeFilter = (key, value) => {
    setRequestData((prev) => ({ ...prev, [key]: value, page: 1 }));

    const newQuery = collectRequestData({ ...requestData, [key]: value, page: 1 });

    dispatch(getRefillsRequest(newQuery));
  };

  const onChangeDate = (currentDate) => {
    const newRequestData = {
      ...requestData, start: currentDate[0] || '', end: currentDate[1] || '', page: 1,
    };

    setRequestData(newRequestData);

    dispatch(getRefillsRequest(collectRequestData(newRequestData)));
  };

  const onChangePageData = async (path, count) => {
    const newRequestData = { ...requestData, [path]: count };

    if (path === 'limit') newRequestData.page = 1;

    setRequestData(newRequestData);

    dispatch(getRefillsRequest(collectRequestData(newRequestData)));
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <div className={classes.filter_button}>
          <FilterButton
            onClick={onTypeClick}
            label="Type"
            activeValue={status}
            buttonsData={typeData}
            disableActiveButton
          />
        </div>

        <div className={classes.date_picker_wrapper}>
          <RangeDatePicker
            currentDate={requestData}
            onChangeDate={onChangeDate}
            showLastYear
            emptyAfterClear
          />
        </div>

        <div className={classes.input_filter_wrapper}>
          <InputField
            label="Search by text"
            value={textSearch}
            onChange={onSearchChange}
          />
        </div>

        <div className={classes.input_filter_wrapper}>
          <Select
            label="Rep"
            nextOptionListRequest={nextOptionListRequest}
            options={selectRepList}
            totalPage={pageCount}
            currentPage={currentPage}
            limit={20}
            currentOption={requestData.rep}
            onChange={(option) => onChangeFilter('rep', option)}
            optionPathname="name"
          />
        </div>

        <div className={classes.input_filter_wrapper}>
          <Select
            label="All specializations"
            options={specOptions}
            currentOption={spec}
            onChange={(option) => onChangeFilter('spec', option)}
          />
        </div>

        {isAdmin && (
          <div className={classes.input_filter_wrapper}>
            <Select
              label="All health plans"
              nextOptionListRequest={nextHealthPlanOptionListRequest}
              options={selectHealthList}
              totalPage={healthTotalPage}
              currentPage={healthCurrentPage}
              limit={healthPlanLimit}
              currentOption={hub}
              onChange={(option) => onChangeFilter('hub', option)}
              optionPathname="hubId"
            />
          </div>
        )}

        {/* <div className={classes.button_wrapper}> */}
        {/*  <Button */}
        {/*    className={classes.updateButton} */}
        {/*    title="Assign to Me" */}
        {/*    type="submit" */}
        {/*    reverseColor */}
        {/*    onClick={() => {}} */}
        {/*  /> */}
        {/* </div> */}
      </div>

      {requestStatus !== 'success'
        ? <div className={classes.loader}><Loader /></div>
        : (
          <div className={classes.table_wrapper}>
            <RefilesTable
              header={header}
              onChange={onChangePageData}
              page={+page - 1}
              limit={+limit}
              totalPage={+refillTotalPage}
              data={refillsList}
              className={classes.table_tr}
              classFax={classes.table_fax}
              status={status}
            />
          </div>
        )}
    </div>
  );
}

export default Refills;
